import TalentAddressUnitInput from 'components/TalentAddressUnitInput';
import TalentInvoiceInformation from 'components/TalentInvoiceInformation';
import { composeTalentAddress, splitTalentAddress, paymentEmailLabel } from 'utils/talentAddress';
import type { InvoicePaymentAuditFields } from 'components/InvoicePaymentAudit';
import InvoiceNonPaymentActions from 'components/InvoiceNonPaymentActions';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import dayjs from 'dayjs';
import { MenuItem, CircularProgress, Snackbar, Alert } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { FormMaterialSelector } from "components/Selector";
import Api from 'api';
import useActionApi from 'hooks/useActionApi';
import useInvoiceDisputeActor from 'hooks/useInvoiceDisputeActor';
import useStripeExpressLink from 'hooks/useStripeExpressLink';
import { hasStripeExpressSetUp } from 'utils/stripeExpressSetUp';
import StripeExpressOpenError from 'components/StripeExpressOpenError';
import TalentPaymentStatusDetails from './components/TalentPaymentStatusDetails';
import FixedLoader from 'components/FixedLoader';
import { getPaymentTerms } from 'utils';
import { formatBillableDuration } from 'utils/billableDuration';
import { CHAT_POST_SHIFT_NOTE, shouldShowPostShiftChatNote } from 'utils/shiftChat';
import { Button } from '@mui/material';
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';
import { SimpleModal } from 'components/Modal';
import { CrossButtonBlack } from 'components/Buttons/CrossButton';
import TalentNavbar from 'components/TalentNavbar';
import {
    TitleContainer,
    PopupTitle,
    ModalText,
} from '../ContactSupport/ContactSupport.styles';

import {
    PageContainer,
    TimeInputField,
    BillingRow,
    PaymentDetailRow,
    PaymentDetailValue,
    PaymentStatusRow,
    SectionContainer,
    InlineEditContainer,
    CompactFormControl,
    HeaderContainer,
    GHRNotificationRow,
    StripeWarningText,
} from './Invoice.styles';

import { Edit as EditIcon, Save as SaveIcon, Close as CloseIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import MaterialInput from "components/Inputs/MaterialInput";
import { Show } from 'components/Conditionals';
import { getTalentPaymentViewState } from 'utils/talentPaymentState';
import { deliverInvoicePdf } from './invoicePdfDownload';
import InvoiceTimeSelector from './InvoiceTimeSelector';
import { calculateInvoiceBreakdown, formatInvoiceTime, formatUnpaidMinutes, INVOICE_UNPAID_MINUTES, invoiceDurationPickerValue, parseInvoiceTime } from './invoiceTimeOptions';

interface AutocompleteProps {
    buildingNumber?: string
    state: string,
    city?: string,
    postalCode?: string,
    address?: string,
    valueEqual?: boolean
}

interface Shift {
  postedStart: string;
  postedEnd: string;
  actualStart: string;
  actualEnd: string;
  unpaidHours: number | null;
  gapUnpaid: boolean;
}

interface JobLocation {
  practiceName: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  lat?: number;
  lon?: number;
}

interface Hygienist {
  city?: string;
  province?: string;
  postalCode?: string;
  address: string;
  etransferEmail: string;
  country: string;
  stripeCreatedTimestamp?: string | null;
}

interface JobInvoice extends InvoicePaymentAuditFields {
  creditPaid?: number;
  nonPaymentReportedOn?: string | null;
  nonPaymentEscalatedOn?: string | null;
  totalHours: number;
  totalUnpaidHours: number;
  totalBillableHours: number;
  lunchHours?: number | null;
  downtimeHours?: number | null;
  totalInvoiceAmt: number;
  hourlyRate: number;
  sentOn: string;
  id: number;
  hygienistMarkedPaid: boolean | number;
  dentistMarkedPaid?: boolean | number;
  paidOn?: string | null;
  paymentLifecycleId?: number;
  officePaymentStatus?: 'processing' | 'action_required' | 'paid';
  talentPaymentStatus?: 'not_started' | 'processing' | 'action_required' | 'paid';
  talentPaymentMethod?: 'instant' | 'standard' | null;
}

interface Job {
  id: number;
  shifts: Shift[];
  hourlyRate: number;
  location: JobLocation;
  Autobook: number;
  hygienist: Hygienist;
  invoice?: JobInvoice;
  paymentTerms: number;
  ghr?: number;
  stripeActiveStatus: boolean;
  proposedRate: number;
}

interface LocationState {
  jobId?: number;
  job?: Job;
  isViewInvoice?: boolean;
}

const HygienistInvoiceContent: React.FC<{ job: Job; isViewInvoice?: boolean; isVisitCurrent: () => boolean }> = ({ job: originalJob, isViewInvoice = false, isVisitCurrent }) => {
    const [freshInvoice, setFreshInvoice] = useState(originalJob.invoice);
    const jobData = { ...originalJob, invoice: freshInvoice };
    useEffect(() => setFreshInvoice(originalJob.invoice), [originalJob.invoice, originalJob.id]);
    const activeInvoiceJob = useRef<number | null>(originalJob.id);
    activeInvoiceJob.current = originalJob.id;
    useEffect(() => () => { activeInvoiceJob.current = null; }, []);
    const isCurrent = useCallback(() => activeInvoiceJob.current === originalJob.id && isVisitCurrent(), [originalJob.id, isVisitCurrent]);
    const refreshInvoice = async () => {
        if (!isCurrent()) return;
        const jobId = originalJob.id;
        const fresh = await Api.getInvoiceForJob(jobId);
        if (isCurrent()) setFreshInvoice(fresh as any);
    };
    const history = useHistory();
    const [isEdit, setIsEdit] = useState(false);
    const isLegacyInvoice = isViewInvoice && !!jobData.invoice && jobData.invoice.lunchHours == null && jobData.invoice.downtimeHours == null;
    const requiresDowntime = !isLegacyInvoice && Number(jobData.shifts[0].gapUnpaid) === 1;
    
    // Get current user info for navbar
    const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
    const hygienist = currentUser?.hygienist;
    const firstName = hygienist?.firstName || '';
    const lastName = hygienist?.lastName || '';
    const photoUrl = hygienist?.photoUrl || undefined;
    const isHygOrAsst = hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.HYGIENIST || 
                        hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.ASSISTANT;
    const parseTimeString = useCallback((timeStr: string) => {
        const normalizedTimeStr = timeStr.toLowerCase().trim();

        if (normalizedTimeStr.includes('gmt')) {
            return new Date(timeStr);
        }

        const [time, period] = normalizedTimeStr.split(/\s+/);
        let [hours, minutes] = time.split(':').map(Number);

        if (period === 'pm' && hours !== 12) {
            hours += 12;
        } else if (period === 'am' && hours === 12) {
            hours = 0;
        }

        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        return date;
    }, []);

    const formatDatabaseTimeForSelector = useCallback((dbTime: string) => {
        const parsed = parseInvoiceTime(dbTime);
        return parsed ? formatInvoiceTime(parsed) : '';
    }, []);

    const defaultFormValues = useMemo(() => ({
        startTime: formatDatabaseTimeForSelector((isViewInvoice || isEdit) ? jobData.shifts[0].actualStart : jobData.shifts[0].postedStart),
        endTime: formatDatabaseTimeForSelector((isViewInvoice || isEdit) ? jobData.shifts[0].actualEnd : jobData.shifts[0].postedEnd),
        unpaidTime: isViewInvoice && jobData.invoice ? (isLegacyInvoice ? String(jobData.shifts[0].unpaidHours ?? '') : invoiceDurationPickerValue(jobData.invoice.lunchHours)) : '',
        downtimeTime: isViewInvoice ? invoiceDurationPickerValue(jobData.invoice?.downtimeHours) : '',
    }), [formatDatabaseTimeForSelector, isViewInvoice, isEdit, isLegacyInvoice, jobData.shifts, jobData.invoice]);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
        trigger,
        setError
    } = useForm({
        defaultValues: defaultFormValues,
        mode: 'onChange'
    });

    const startTime = useWatch({ control, name: 'startTime' });
    const endTime = useWatch({ control, name: 'endTime' });
    const unpaidTime = useWatch({ control, name: 'unpaidTime' });
    const downtimeTime = useWatch({ control, name: 'downtimeTime' });

    const [totalHours, setTotalHours] = useState<number>(0);
    const [billableHours, setBillableHours] = useState<number>(0);
    const [totalAmount, setTotalAmount] = useState<number>(0);
    const [travelAllowance, setTravelAllowance] = useState<number>(0);
    const [showTimeWarningPopup, setShowTimeWarningPopup] = useState(false);
    const [showStripeModal, setShowStripeModal] = useState(false);
    const [isPostInvoiceStripeModal, setIsPostInvoiceStripeModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [hasConfirmedEarlyTime, setHasConfirmedEarlyTime] = useState(false);
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [isEditingETransfer, setIsEditingETransfer] = useState(false);
    const [editedAddress, setEditedAddress] = useState(jobData.hygienist?.address ?? '');
    const [addressUnit, setAddressUnit] = useState(splitTalentAddress(jobData.hygienist?.address).unit);
    const [editedAddressFields, setEditedAddressFields] = useState({city: jobData.hygienist?.city, state: jobData.hygienist?.province, postalCode: jobData.hygienist?.postalCode});
    const [editedETransfer, setEditedETransfer] = useState(jobData.hygienist?.etransferEmail ?? '');
    const [tempETransfer, setTempETransfer] = useState(jobData.hygienist?.etransferEmail ?? '');
    const [autocompletedAddress, setAutocompletedAddress] = useState<AutocompleteProps | undefined>();
    const [addressError, setAddressError] = useState<string>('');
    const [isPaid, setIsPaid] = useState(
        getTalentPaymentViewState(jobData.invoice).isPaid
    );
    const paymentView = useMemo(() => getTalentPaymentViewState({
        ...jobData.invoice,
        hygienistMarkedPaid: isPaid,
    }), [jobData.invoice, isPaid]);
    useEffect(() => {
        setIsPaid(getTalentPaymentViewState(jobData.invoice).isPaid);
    }, [jobData.invoice]);
    const [distance, setDistance] = useState<number | null>(null);
    const [isInvoiceButtonDisabled, setIsInvoiceButtonDisabled] = useState(false);
    const [buttonDisableTimer, setButtonDisableTimer] = useState<NodeJS.Timeout | null>(null);
    const [stripeAccountStatus, setStripeAccountStatus] = useState<{
        isActive: boolean;
        status: string;
        message: string;
        payoutsEnabled: boolean;
        requirementsDue: string[];
        accountId?: string;
        error?: string;
    } | null>(null);
    const [showStripeUpdateModal, setShowStripeUpdateModal] = useState(false);
    const {
        opening: stripeOpening,
        failure: stripeOpenFailure,
        open: openStripeExpressLink,
        clearFailure: clearStripeOpenFailure,
    } = useStripeExpressLink();
    const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [isDownloadErrorOpen, setIsDownloadErrorOpen] = useState(false);
    const isGHR = !!jobData?.ghr;

    const isEndTimeValid = useCallback((endTimeStr: string, startTimeStr: string) => {
        if (!startTimeStr || !endTimeStr) return true;
        const startDate = parseTimeString(startTimeStr);
        const endDate = parseTimeString(endTimeStr);
        return endDate > startDate;
    }, [parseTimeString]);

    const formatDisplayHours = useCallback((value: number) => {
        const hours = Math.floor(value);
        const minutes = Math.round((value - hours) * 60);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }, []);

    const formatTimeDisplay = useCallback((timeStr: string) => {
        if (!timeStr) return '';

        if (timeStr.match(/^\d{1,2}:\d{2}\s?(?:AM|PM|am|pm)$/)) {
            return timeStr;
        }

        try {
            const date = new Date(timeStr);
            return date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } catch (error) {
            return timeStr;
        }
    }, []);

    const formatTimeForDatabase = useCallback((timeStr: string) => {
        if (!timeStr) return '';

        try {
            if (timeStr.match(/^\d{1,2}:\d{2}\s?(?:AM|PM|am|pm)$/)) {
                const [time, period] = timeStr.toLowerCase().trim().split(/\s+/);
                const [hours, minutes] = time.split(':').map(Number);
                const hour12 = period === 'pm' && hours !== 12 ? hours % 12 : hours === 12 && period === 'am' ? 0 : hours;
                return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
            }

            const date = new Date(timeStr);
            if (!isNaN(date.getTime())) {
                const hours = date.getHours();
                const minutes = date.getMinutes();
                const period = hours >= 12 ? 'pm' : 'am';
                const hour12 = hours % 12 || 12;
                return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
            }

            return '';
        } catch (error) {
            console.error('Error formatting time for database:', error);
            return '';
        }
    }, []);

    useEffect(() => {
        if (isEditingAddress) {
            const parsed = splitTalentAddress(editedAddress);
            setAddressUnit(parsed.unit);
            setAutocompletedAddress({ ...editedAddressFields, state: editedAddressFields.state || '', address: parsed.street, valueEqual: true, buildingNumber: 'existing' });
            // @ts-ignore
            const TempStars: any = window.TempStars;
            TempStars.Autocomplete.init(
                { country: jobData.hygienist?.country },
                ({ state, city, postalCode, address, buildingNumber, valueEqual }: AutocompleteProps) => {
                    const parsed = splitTalentAddress(address);
                    if (valueEqual) setAddressUnit(current => current || parsed.unit);
                    setAutocompletedAddress({ state, city, postalCode, address: parsed.street, buildingNumber, valueEqual });
                }
            );

            return () => TempStars.Autocomplete.removeListener();
        }
    }, [isEditingAddress, jobData.hygienist?.country]);

    const compareTimeStrings = useCallback((time1: string, time2: string) => {
        const selected = parseInvoiceTime(time1);
        if (!selected) return 0;
        const hours1 = selected.hour;
        const minutes1 = selected.minute;

        const [timeStr, period] = time2.toLowerCase().split(' ');
        let [hours2, minutes2] = timeStr.split(':').map(Number);

        if (period === 'pm' && hours2 !== 12) {
            hours2 += 12;
        } else if (period === 'am' && hours2 === 12) {
            hours2 = 0;
        }

        if (hours1 !== hours2) {
            return hours1 - hours2;
        }
        return minutes1 - minutes2;
    }, []);

    const calculateTravelAllowance = useCallback((distanceInKm: number): number => {
        if (distanceInKm <= 20) {
            return 0;
        } else if (distanceInKm < 46) {
            return 7.50;
        } else if (distanceInKm < 76) {
            return 15.00;
        } else if (distanceInKm < 91) {
            return 25.00;
        } else {
            return 30.00;
        }
    }, []);

    const [getDistanceMatrixLoading, , getDistanceMatrix] = useActionApi({
        apiMethod: Api.getDistanceMatrix,
        onSuccess: (data: any) => {
            const distanceInKm = (data as { distance: { value: number } }).distance.value / 1000;
            setDistance(distanceInKm);
        }
    });

    useEffect(() => {
        const fetchDistance = async () => {
            if (jobData.Autobook === 1 && jobData.location?.lat && jobData.location?.lon) {
                try {
                    await getDistanceMatrix({
                        lat: jobData.location.lat,
                        lng: jobData.location.lon
                    });
                } catch (error) {
                    console.error('Error calculating distance:', error);
                    setDistance(null);
                }
            }
        };

        fetchDistance();
    }, [jobData.Autobook, jobData.location]);

    useEffect(() => {
        if (jobData.Autobook === 1 && distance !== null) {
            const allowance = calculateTravelAllowance(distance);
            setTravelAllowance(allowance);
        } else {
            setTravelAllowance(0);
        }
    }, [distance, jobData.Autobook]);

    useEffect(() => {
        const fetchStripeStatus = async () => {
            try {
                const status = await Api.checkStripeAccountStatus();
                setStripeAccountStatus(status);
            } catch (error) {
                console.error('Error fetching Stripe account status:', error);
                setStripeAccountStatus({
                    isActive: false,
                    status: 'error',
                    message: 'Error checking Stripe status',
                    payoutsEnabled: false,
                    requirementsDue: []
                });
            }
        };

        fetchStripeStatus();
    }, []);

    // Cleanup timer on component unmount
    useEffect(() => {
        return () => {
            if (buttonDisableTimer) {
                clearTimeout(buttonDisableTimer);
            }
        };
    }, [buttonDisableTimer]);

    useEffect(() => {
        if (startTime && endTime) {
            if (!isEndTimeValid(endTime, startTime)) {
                setValue('endTime', '');
                return;
            }

            try {
                const startDate = parseTimeString(startTime);
                const endDate = parseTimeString(endTime);

                if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                    throw new Error("Invalid date parsing");
                }

                const diffMinutes = (endDate.getTime() - startDate.getTime()) / (1000 * 60);
                const totalHoursDecimal = diffMinutes / 60;
                setTotalHours(totalHoursDecimal);

                const lunchHours = parseFloat(unpaidTime || '0') || 0;
                const downtimeHours = parseFloat(downtimeTime || '0') || 0;
                const { billableHours: billableHoursDecimal } = calculateInvoiceBreakdown(
                    totalHoursDecimal, lunchHours, requiresDowntime ? downtimeHours : 0,
                    !!jobData.shifts[0].gapUnpaid,
                );
                setBillableHours(billableHoursDecimal);

                const rate = jobData.hourlyRate;
                const billableAmount = billableHoursDecimal * rate;
                setTotalAmount(Math.round((billableAmount + travelAllowance) * 100) / 100);
            } catch (error) {
                console.error("Time calculation error:", error);
                setTotalHours(0);
                setBillableHours(0);
                setTotalAmount(0);
            }
        }
    }, [startTime, endTime, unpaidTime, downtimeTime, requiresDowntime, travelAllowance, jobData.hourlyRate, jobData.shifts, parseTimeString, isEndTimeValid, setValue]);

    // T2-7437: open Stripe in the system browser (iOS ignores window.open) and
    // keep the invoice open; on failure the dialog stays with the reason. The
    // visit guard is asked again once the link arrives, so nothing opens after
    // the talent left this invoice or the account changed.
    const handleStripeAccountUpdate = useCallback(async () => {
        if (!isCurrent()) return;
        if (await openStripeExpressLink(isCurrent) && isCurrent()) {
            setShowStripeUpdateModal(false);
        }
    }, [openStripeExpressLink, isCurrent]);

    const openStripeUpdateModal = useCallback(() => {
        clearStripeOpenFailure();
        setShowStripeUpdateModal(true);
    }, [clearStripeOpenFailure]);

    const closeStripeUpdateModal = useCallback(() => {
        clearStripeOpenFailure();
        setShowStripeUpdateModal(false);
    }, [clearStripeOpenFailure]);

    const handleCreateStripeModal = useCallback(() => {
        clearStripeOpenFailure();
        setIsPostInvoiceStripeModal(false);
        setShowStripeModal(true);
    }, [clearStripeOpenFailure]);

    // T2-7369: OK opens Stripe Express set-up in the browser instead of leaving
    // for Profile, so the unsent invoice and everything entered stay on screen.
    // Like every handler here, it acts only while this invoice visit is current.
    const handleStripeSetupOk = useCallback(async () => {
        if (!isCurrent()) return;
        if (!(await openStripeExpressLink(isCurrent)) || !isCurrent()) return;
        setShowStripeModal(false);
        if (isPostInvoiceStripeModal) {
            setShowSuccessModal(true);
        }
    }, [openStripeExpressLink, isPostInvoiceStripeModal, isCurrent]);

    const handleStripeSetupClose = useCallback(() => {
        clearStripeOpenFailure();
        setShowStripeModal(false);
        if (isPostInvoiceStripeModal) {
            setShowSuccessModal(true);
        }
    }, [clearStripeOpenFailure, isPostInvoiceStripeModal]);

    // T2-7370: Home's "Create and Send Invoice" prompt opens this page with the calendar's job, which
    // carries neither stripeActiveStatus nor the temp's Stripe timestamp. The job-details read has
    // stripeActiveStatus for the temp's own job, so ask it before deciding on the post-invoice prompt.
    // Like every read on this page, it is skipped once the invoice visit is no longer current.
    const stripeSetUpLookup = useRef<Promise<boolean> | null>(null);
    useEffect(() => {
        const isKnown = typeof jobData?.stripeActiveStatus === 'boolean'
            || jobData?.hygienist?.stripeCreatedTimestamp != null;
        if (isKnown || !jobData?.id || stripeSetUpLookup.current || !isCurrent()) return;
        stripeSetUpLookup.current = Api.getJobDetails(jobData.id)
            .then((details: { stripeActiveStatus?: unknown } | null) => details?.stripeActiveStatus === true)
            .catch(() => false);
    }, [jobData?.id, jobData?.stripeActiveStatus, jobData?.hygienist?.stripeCreatedTimestamp, isCurrent]);

    /** Determine post-invoice flow: skip Stripe modal for users who already have Stripe Express (T2-7370) */
    const onInvoiceSuccess = useCallback(async () => {
        if (!isCurrent()) return;
        const isSetUp = hasStripeExpressSetUp(jobData)
            || (stripeSetUpLookup.current ? await stripeSetUpLookup.current : false);
        if (!isCurrent()) return;
        if (isSetUp) {
            setShowSuccessModal(true);
            return;
        }

        clearStripeOpenFailure();
        setIsPostInvoiceStripeModal(true);
        setShowStripeModal(true);
    }, [jobData, isCurrent, clearStripeOpenFailure]);

    /** Dismiss success modal and navigate to home */
    const handleSuccessDismiss = useCallback(() => {
        if (!isCurrent()) return;
        setShowSuccessModal(false);
        (window as any).TempStars?.App?.goReact('hygienistHome');
    }, [isCurrent]);

    const [createInvoiceLoading, , createInvoice] = useActionApi({
        apiMethod: Api.sendInvoice,
        onSuccess: onInvoiceSuccess
    });

    const [resendInvoiceLoading, , resendInvoice] = useActionApi({
        apiMethod: Api.resendInvoice,
        onSuccess: onInvoiceSuccess
    });

    const [updateInvoiceLoading, , updateInvoice] = useActionApi({
        apiMethod: Api.updateInvoice,
        onSuccess: () => {
        }
    });

    const prepareResendInvoiceData = useCallback(async () => {
        const useAdjustedTimes = startTime && endTime;
        const lunchHours = parseFloat(unpaidTime || '0') || 0;
        const downtimeHours = parseFloat(downtimeTime || '0') || 0;
        const breakdown = calculateInvoiceBreakdown(totalHours, lunchHours, downtimeHours, !!jobData.shifts[0].gapUnpaid);

        let calculatedData;
        if (useAdjustedTimes) {
            calculatedData = {
                actualStart: formatTimeForDatabase(startTime),
                actualEnd: formatTimeForDatabase(endTime),
                totalHours,
                unpaidHours: isLegacyInvoice ? lunchHours : breakdown.unpaidHours,
                billableHours,
                travelAllowance,
                totalAmt: totalAmount,
            };
        } else {
            calculatedData = {
                actualStart: jobData.shifts[0].actualStart,
                actualEnd: jobData.shifts[0].actualEnd,
                totalHours: jobData.invoice?.totalHours,
                unpaidHours: jobData.invoice?.totalUnpaidHours,
                billableHours: jobData.invoice?.totalBillableHours,
                travelAllowance,
                totalAmt: jobData.invoice?.totalInvoiceAmt,
            };
        }

        const data = {
            jobId: jobData.id,
            actualStart: calculatedData.actualStart,
            actualEnd: calculatedData.actualEnd,
            totalHours: calculatedData.totalHours,
            unpaidHours: calculatedData.unpaidHours,
            billableHours: calculatedData.billableHours,
            totalAmt: calculatedData.totalAmt,
            hourlyRate: jobData.invoice?.hourlyRate,
            travelAllowance: calculatedData.travelAllowance,
            sentOn: dayjs(jobData.invoice?.sentOn).format('MMM D, YYYY'),
            id: jobData.invoice?.id,
            ...(!isLegacyInvoice ? { lunchHours, ...(requiresDowntime ? { downtimeHours } : {}) } : {}),
        };

        return data;
    }, [startTime, endTime, totalHours, unpaidTime, downtimeTime, isLegacyInvoice, requiresDowntime, jobData, billableHours, travelAllowance, totalAmount, formatTimeForDatabase]);

    const handleCreateInvoice = useCallback(async () => {
        if (!isCurrent() || !startTime || !endTime) return;

        const scrollToField = (fieldName: string) => {
            const el = document.querySelector(`[name="${fieldName}"]`) as HTMLElement | null;
            const target = (el?.closest('.MuiFormControl-root') as HTMLElement | null) || el;
            try { target?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) {}
        };

        // On mobile (Cordova/Framework7), selects may not blur before click; ensure RHF sees current value.
        // Trigger and focus the field to force immediate validation feedback.
        const isUnpaidValid = await trigger('unpaidTime');
        if (!isCurrent()) return;
        if (!isUnpaidValid) {
            scrollToField('unpaidTime');
            try {
                // Toggle a minor vibrate feedback on supported devices to draw attention
                // @ts-ignore
                // eslint-disable-next-line compat/compat -- feature-detected; iOS has no vibrate
                if (navigator && navigator.vibrate) { navigator.vibrate(30); }
            } catch (_) {}
            return;
        }
        if (requiresDowntime) {
            const isDowntimeValid = await trigger('downtimeTime');
            if (!isCurrent()) return;
            if (!isDowntimeValid) {
                scrollToField('downtimeTime');
                return;
            }
        }
        const lunchHours = parseFloat(unpaidTime || '0') || 0;
        const downtimeHours = parseFloat(downtimeTime || '0') || 0;
        const breakdown = calculateInvoiceBreakdown(totalHours, lunchHours, downtimeHours, !!jobData.shifts[0].gapUnpaid);
        if (lunchHours + (requiresDowntime ? downtimeHours : 0) > totalHours) {
            const field = requiresDowntime ? 'downtimeTime' : 'unpaidTime';
            setError(field, { type: 'validate', message: requiresDowntime ? 'Lunch and downtime cannot exceed shift duration.' : 'Lunch cannot exceed shift duration.' });
            scrollToField(field);
            return;
        }

        if (isInvoiceButtonDisabled) return;
        
        setIsInvoiceButtonDisabled(true);
        const timer = setTimeout(() => {
            setIsInvoiceButtonDisabled(false);
            setButtonDisableTimer(null);
        }, 5000);
        setButtonDisableTimer(timer);

        try {
            if (isViewInvoice) {
                const data = await prepareResendInvoiceData();
                if (!isCurrent()) return;
                await resendInvoice(data);
                if (!isCurrent()) return;
                history.push('loading');
                ;(window as any).TempStars?.App?.goReact('hygienistHome');
            } else {
                const invoiceData = {
                    jobId: jobData.id,
                    actualStart: formatTimeForDatabase(startTime),
                    actualEnd: formatTimeForDatabase(endTime),
                    unpaidHours: isLegacyInvoice ? lunchHours : breakdown.unpaidHours,
                    ...(!isLegacyInvoice ? { lunchHours, ...(requiresDowntime ? { downtimeHours } : {}) } : {}),
                    travelAllowance,
                    totalHours,
                    billableHours,
                    totalAmt: totalAmount,
                    hourlyRate: isGHR ? jobData?.proposedRate : jobData?.hourlyRate,
                };

                await createInvoice(invoiceData);
            }
        } catch (error) {
            console.error('Error handling invoice:', error);
        }
    }, [startTime, endTime, unpaidTime, downtimeTime, isLegacyInvoice, requiresDowntime, isViewInvoice, prepareResendInvoiceData, resendInvoice, history, jobData, formatTimeForDatabase, travelAllowance, totalHours, billableHours, totalAmount, createInvoice, isInvoiceButtonDisabled, setError, trigger, isCurrent]);

    const checkStartTimeChange = useCallback((newStartTime: string) => {
        const selectedTime = newStartTime;
        const postedStart = jobData.shifts[0].postedStart;

        const selectedDate = parseTimeString(selectedTime);
        const postedStartDate = parseTimeString(postedStart);
        const oneHourBefore = new Date(postedStartDate);
        oneHourBefore.setHours(oneHourBefore.getHours() - 1);

        if (selectedDate < oneHourBefore) {
            setValue('startTime', oneHourBefore.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            }));
            return;
        }

        if (!hasConfirmedEarlyTime && compareTimeStrings(selectedTime, postedStart) < 0) {
            setShowTimeWarningPopup(true);
            setValue('startTime', selectedTime);
            return;
        }
        setValue('startTime', selectedTime);
    }, [jobData.shifts, parseTimeString, hasConfirmedEarlyTime, compareTimeStrings, setValue]);

    const handleAddressUpdate = useCallback(async () => {
        if (!isCurrent()) return;
        if (!autocompletedAddress?.valueEqual) {
            setAddressError('Please select your address from the list');
            return;
        }
        if (!autocompletedAddress?.buildingNumber?.length) {
            setAddressError('Add your building #, street');
            return;
        }

        try {
            const address = composeTalentAddress(autocompletedAddress.address, addressUnit);
            await Api.updateHygienistAccount({
                address,
                city: autocompletedAddress.city,
                province: autocompletedAddress.state,
                postalCode: autocompletedAddress.postalCode
            });
            if (!isCurrent()) return;
            setEditedAddress(address);
            setEditedAddressFields({ city: autocompletedAddress.city, state: autocompletedAddress.state, postalCode: autocompletedAddress.postalCode });
            setIsEditingAddress(false);
            setAddressError('');
        } catch (error) {
            setAddressError(error instanceof Error ? error.message : 'Failed to update address');
        }
    }, [autocompletedAddress, addressUnit, isCurrent]);

    const handleETransferUpdate = useCallback(async () => {
        if (!isCurrent()) return;
        try {
            await Api.updateHygienistAccount({
                etransferEmail: tempETransfer
            });
            if (!isCurrent()) return;
            setEditedETransfer(tempETransfer);
            setIsEditingETransfer(false);
        } catch (error) {
            console.error('Error updating e-transfer:', error);
        }
    }, [tempETransfer, isCurrent]);

    const handlePaidToggle = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!isCurrent() || !getTalentPaymentViewState(jobData.invoice).canUseLegacyPaidToggle) return;
        const isChecked = event.target.checked;
        try {
            await Api.updateInvoice({ id: jobData.invoice?.id, hygienistMarkedPaid: isChecked ? 1 : 0 });
            await refreshInvoice();
        } catch (error) {
            console.error('Error updating paid status:', error);
            setIsPaid(!isChecked);
        }
    }, [jobData.invoice, updateInvoice, isCurrent]);

    const getInvoiceFilename = useCallback(() => {
        const dateSource = (jobData as any)?.startDate || (jobData.shifts?.[0] as any)?.shiftDate || jobData.invoice?.sentOn;
        const formattedDate = dateSource ? dayjs(dateSource).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
        return `Invoice_${jobData.id}_${formattedDate}.pdf`;
    }, [jobData.id, jobData.invoice?.sentOn, jobData.shifts]);

    const handleDownloadInvoicePdf = useCallback(async () => {
        if (!isCurrent() || !jobData.invoice?.id) return;
        setDownloadError(null);
        setIsDownloadErrorOpen(false);
        setIsDownloadingInvoice(true);
        try {
            const blob = await Api.downloadInvoicePdf(jobData.invoice.id);
            if (!isCurrent()) return;
            await deliverInvoicePdf(blob, getInvoiceFilename());
        } catch (error: any) {
            const fallbackMessage = typeof error?.message === 'string' && error.message.length > 0
                ? error.message
                : 'Unable to download invoice. Please try again.';
            setDownloadError(fallbackMessage);
            setIsDownloadErrorOpen(true);
        } finally {
            setIsDownloadingInvoice(false);
        }
    }, [jobData.invoice?.id, getInvoiceFilename, isCurrent]);

    useEffect(() => {
        const startTimeValue = formatDatabaseTimeForSelector((isViewInvoice || isEdit) ? jobData.shifts[0].actualStart : jobData.shifts[0].postedStart);
        const endTimeValue = formatDatabaseTimeForSelector((isViewInvoice || isEdit) ? jobData.shifts[0].actualEnd : jobData.shifts[0].postedEnd);

        if (startTimeValue) setValue('startTime', startTimeValue);
        if (endTimeValue) setValue('endTime', endTimeValue);
        const lunch = isViewInvoice ? jobData.invoice?.lunchHours ?? jobData.shifts[0].unpaidHours : null;
        const downtime = isViewInvoice ? jobData.invoice?.downtimeHours : null;
        setValue('unpaidTime', isLegacyInvoice ? (lunch == null ? '' : String(Number(lunch))) : invoiceDurationPickerValue(lunch));
        setValue('downtimeTime', invoiceDurationPickerValue(downtime));
    }, [isViewInvoice, isEdit, isLegacyInvoice, jobData.shifts, jobData.invoice, formatDatabaseTimeForSelector, setValue]);

    const handleBack = useCallback(() => {
        if (!isCurrent()) return;
        history.push('loading');
        ;(window as any).TempStars?.App?.goReact('hygienistHome');
    }, [history, isCurrent]);

    // Memoize the time selector components to prevent unnecessary re-renders
    const startTimeSelector = useMemo(() => (
        <TimeInputField>
            <InvoiceTimeSelector
                name="startTime"
                label="Arrival Time"
                postedStart={jobData.shifts[0].postedStart}
                value={startTime}
                disabled={isViewInvoice && !isEdit}
                onChange={checkStartTimeChange}
            />
        </TimeInputField>
    ), [isViewInvoice, isEdit, jobData.shifts, startTime, checkStartTimeChange]);

    const endTimeSelector = useMemo(() => (
        <TimeInputField>
            <InvoiceTimeSelector
                name="endTime"
                label="End Time"
                postedStart={jobData.shifts[0].postedStart}
                value={endTime}
                disabled={isViewInvoice && !isEdit}
                onChange={(value) => setValue('endTime', value)}
            />
        </TimeInputField>
    ), [isViewInvoice, isEdit, jobData.shifts, endTime, setValue]);

    const unpaidTimeSelector = useMemo(() => (
        <TimeInputField>
            <FormMaterialSelector
                control={control}
                name="unpaidTime"
                label={isLegacyInvoice ? 'Unpaid Time' : 'Unpaid Lunch'}
                disabled={isViewInvoice && !isEdit}
                rules={{ required: 'Please indicate your unpaid lunch time.' }}
            >
                {[
                    <MenuItem key="default" value="">{isLegacyInvoice ? 'Unpaid Time' : 'Select unpaid lunch'}</MenuItem>,
                    ...INVOICE_UNPAID_MINUTES.map(minutes => (
                        <MenuItem key={minutes} value={String(minutes / 60)}>{formatUnpaidMinutes(minutes)}</MenuItem>
                    ))
                ]}
            </FormMaterialSelector>
            {!isLegacyInvoice && <div className="invoiceTimeHelper">Time you took for a meal break. Lunch is always unpaid.</div>}
        </TimeInputField>
    ), [control, isViewInvoice, isEdit, isLegacyInvoice]);

    const downtimeSelector = useMemo(() => !requiresDowntime ? null : (
        <TimeInputField>
            <FormMaterialSelector
                control={control}
                name="downtimeTime"
                label="Downtime"
                disabled={isViewInvoice && !isEdit}
                rules={{ required: 'Please indicate any downtime.' }}
            >
                {[
                    <MenuItem key="default" value="">Select downtime</MenuItem>,
                    ...INVOICE_UNPAID_MINUTES.map(minutes => (
                        <MenuItem key={minutes} value={String(minutes / 60)}>{formatUnpaidMinutes(minutes)}</MenuItem>
                    ))
                ]}
            </FormMaterialSelector>
            <div className="invoiceTimeHelper">Time you were on-shift but the office had no patients. Downtime is unpaid for this shift. This is NOT the same as lunch — lunch is always unpaid.</div>
        </TimeInputField>
    ), [control, isViewInvoice, isEdit, requiresDowntime]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <>
                <TalentNavbar
                    firstName={firstName}
                    lastName={lastName}
                    photoUrl={photoUrl}
                    isHygOrAsst={isHygOrAsst}
                    instabookActivated={hygienist?.InstabookActivated === 1}
                    isTestAccount={(hygienist as any)?.isTest === 1}
                    showAddShiftButton={false}
                    showJobBoardButton={false}
                />

                <PageContainer>
                    <div className="headerRow">
                        <button 
                            className="backButton no-fastclick" 
                            onClick={handleBack}
                            aria-label="Back"
                        >
                            <ArrowBackIcon />
                            <span>Back</span>
                        </button>
                        <h1 className="pageTitle">
                            {isViewInvoice ? "Invoice Detail" : "Create Invoice"}
                        </h1>
                    </div>
                    <SectionContainer>
                        <HeaderContainer>
                            <div>
                                <h2>{jobData.location.practiceName}</h2>
                                <div>{jobData.location.address}</div>
                                <div>{jobData.location.city}, {jobData.location.province}</div>
                                <div>{jobData.location.postalCode}</div>
                            </div>
                            <div className="invoice-number">
                                Invoice #{jobData.id}
                            </div>
                        </HeaderContainer>
                    </SectionContainer>

                    <SectionContainer>
                        <h2>Completed Shift Details</h2>
                        <div>Start Time: {formatTimeDisplay(jobData.shifts[0].postedStart)}</div>
                        <div>End Time: {formatTimeDisplay(jobData.shifts[0].postedEnd)}</div>
                        <div>Downtime: {jobData.shifts[0].gapUnpaid ? 'Unpaid' : 'Paid'}</div>
                        {!isLegacyInvoice && <div className="invoiceTimeHelper">Note: lunch is always unpaid</div>}
                    </SectionContainer>

                    <SectionContainer>
                        <h2>Actual Shift Details</h2>
                        {startTimeSelector}
                        {endTimeSelector}
                        {unpaidTimeSelector}
                        {downtimeSelector}
                    </SectionContainer>

                    <SectionContainer>
                        <h2>Totals</h2>
                        <BillingRow>
                            <div>Total Time</div>
                            <div>{formatDisplayHours(totalHours)} hrs</div>
                        </BillingRow>
                        <BillingRow className="billable-duration-row">
                            <div>Billable Time</div>
                            <div data-testid="talent-invoice-page-billable-duration">{formatBillableDuration(billableHours)}</div>
                        </BillingRow>
                        <BillingRow>
                            <div>Hourly Rate</div>
                            <div>${jobData.hourlyRate}/hr</div>
                        </BillingRow>
                        <Show if={ isGHR }>
                            <GHRNotificationRow>
                                <div><span>TempStars</span> will separately compensate you <span className="amount">${jobData.ghr!}/hr</span> top-up for this shift (top-up total: ${((jobData?.ghr || 0) * billableHours).toFixed(2)})</div>
                            </GHRNotificationRow>
                        </Show>
                        <BillingRow>
                            <div>Travel Allowance</div>
                            <div>${travelAllowance.toFixed(2)}</div>
                        </BillingRow>
                        <BillingRow isTotalAmount>
                            <div className="total-amount">Total:</div>
                            <div className="total-amount">${totalAmount.toFixed(2)}</div>
                        </BillingRow>
                    </SectionContainer>

                    <SectionContainer isPaymentDetails>
                        <h2>Payment Details</h2>
                        <PaymentDetailRow data-testid="talent-invoice-payment-address-row">
                            <div>Your Mailing Address:</div>
                            <InlineEditContainer>
                                {isEditingAddress ? (
                                    <>
                                        <CompactFormControl>
                                            <MaterialInput
                                                className="no-fastclick"
                                                id="autocomplete"
                                                name="address"
                                                defaultValue={splitTalentAddress(editedAddress).street}
                                                onChange={() => setAutocompletedAddress(undefined)}
                                                type="text"
                                                error={!!addressError}
                                                helperText={addressError}
                                            />
                                        <TalentAddressUnitInput scope="talent-invoice" value={addressUnit} onChange={setAddressUnit} />
                                        </CompactFormControl>
                                        {(!isViewInvoice || isEdit) && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleAddressUpdate}
                                                    className="no-fastclick"
                                                >
                                                    <SaveIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setIsEditingAddress(false);
                                                        setAddressError('');
                                                    }}
                                                    className="no-fastclick"
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <PaymentDetailValue>{editedAddress}</PaymentDetailValue>
                                        {(!isViewInvoice || isEdit) && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setIsEditingAddress(true)}
                                                    className="no-fastclick"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        )}
                                    </>
                                )}
                            </InlineEditContainer>
                        </PaymentDetailRow>
                        <PaymentDetailRow data-testid="talent-invoice-payment-email-row">
                            <div>Your {paymentEmailLabel(jobData.hygienist?.country)}:</div>
                            <InlineEditContainer>
                                {isEditingETransfer ? (
                                    <>
                                        <CompactFormControl>
                                            <MaterialInput
                                                className="no-fastclick"
                                                name="etransfer"
                                                value={tempETransfer}
                                                onChange={(e) => setTempETransfer(e.target.value)}
                                                type="text"
                                            />
                                        </CompactFormControl>
                                        {(!isViewInvoice || isEdit) && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleETransferUpdate}
                                                    className="no-fastclick"
                                                >
                                                    <SaveIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setIsEditingETransfer(false);
                                                        setTempETransfer(editedETransfer);
                                                    }}
                                                    className="no-fastclick"
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <PaymentDetailValue>{editedETransfer}</PaymentDetailValue>
                                        {(!isViewInvoice || isEdit) && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setIsEditingETransfer(true)}
                                                    className="no-fastclick"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        )}
                                    </>
                                )}
                            </InlineEditContainer>
                        </PaymentDetailRow>
                        <BillingRow>
                            <div>Stripe Express Payments:</div>
                            <div>
                                <Show if={stripeAccountStatus == null}>
                                    <div>Loading...</div>
                                </Show>

                                <Show if={stripeAccountStatus != null}>
                                    <Show if={!!stripeAccountStatus?.isActive}>
                                        <div className="stripe-active">Active</div>
                                    </Show>
                                    <Show if={stripeAccountStatus?.status === 'needs_attention'}>
                                        <div className="stripe-payments-stopped">
                                            Payments Stopped
                                        </div>
                                    </Show>
                                    <Show if={stripeAccountStatus?.status === 'inactive'}>
                                        <Button
                                            variant="text"
                                            className="stripe-inactive no-fastclick"
                                            onClick={handleCreateStripeModal}
                                            data-testid="talent-invoice-stripe-setup-link"
                                            sx={{
                                                padding: 0,
                                                textTransform: 'none',
                                                color: isGHR ? '#d32f2f !important' : 'inherit',
                                                fontWeight: isGHR ? 600 : 400,
                                                '&:hover': {
                                                    background: 'none'
                                                }
                                            }}
                                        >
                                            <Show if={isGHR}>
                                                Important, be sure to set up Stripe Express
                                            </Show>
                                            <Show if={!isGHR}>
                                                Get paid faster, set up Stripe Express here
                                            </Show>
                                        </Button>
                                    </Show>

                                </Show>
                            </div>
                        </BillingRow>
                        {stripeAccountStatus?.status === 'needs_attention' && (
                            <BillingRow>
                                <div></div>
                                <StripeWarningText>
                                    Your Stripe Express account needs updating to continue receiving payouts.{' '}
                                    <Button
                                        variant="text"
                                        onClick={openStripeUpdateModal}
                                        data-testid="talent-invoice-stripe-check-button"
                                        sx={{
                                            padding: 0,
                                            textDecoration: 'underline',
                                            textTransform: 'none',
                                            fontSize: '0.875rem',
                                            color: '#d32f2f',
                                            '&:hover': {
                                                background: 'none'
                                            }
                                        }}
                                    >
                                        Click here to check
                                    </Button>
                                </StripeWarningText>
                            </BillingRow>
                        )}
                        <BillingRow>
                            <div>Payment Terms:</div>
                            <div>
                                {getPaymentTerms(jobData.paymentTerms)}
                            </div>
                        </BillingRow>
                        <BillingRow>
                            <div>Expect Payment By:</div>
                            <div>{dayjs(jobData.invoice?.sentOn ? jobData.invoice.sentOn : dayjs()).add(jobData.paymentTerms, 'day').format('YYYY-MM-DD')}</div>
                        </BillingRow>
                        {shouldShowPostShiftChatNote(jobData) && (
                            <div style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.4, marginTop: '8px' }}>
                                {CHAT_POST_SHIFT_NOTE}
                            </div>
                        )}
                        {isViewInvoice && (
                            <PaymentStatusRow>
                                <div>Payment Status:</div>
                                <TalentPaymentStatusDetails
                                    paymentView={paymentView}
                                    testIdPrefix="talent-invoice"
                                    onTogglePaid={handlePaidToggle}
                                />
                                <TalentInvoiceInformation invoiceId={Number(jobData.invoice?.id)} settled={isPaid || Number(jobData.invoice?.dentistMarkedPaid) > 0 || Number(jobData.invoice?.creditPaid) > 0} />
                                <InvoiceNonPaymentActions invoice={jobData.invoice} paymentTerms={jobData.paymentTerms}
                                  isCentralBooked={(jobData.location as any).isCentralBooked} onRefresh={refreshInvoice} />
                            </PaymentStatusRow>
                        )}
                    </SectionContainer>

                    {!isViewInvoice ? (
                        <StyledButton
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleCreateInvoice}
                            disabled={!startTime || !endTime || isInvoiceButtonDisabled}
                            className="no-fastclick"
                            startIcon={isInvoiceButtonDisabled ? <CircularProgress size={20} color="inherit" /> : undefined}
                        >
                            {isInvoiceButtonDisabled ? 'Creating Invoice...' : 'Create Invoice'}
                        </StyledButton>
                    ) : (
                        <>
                            <StyledButton
                                data-testid="talent-invoice-resend"
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleCreateInvoice}
                                className="no-fastclick"
                                disabled={!paymentView.canResendInvoice || isInvoiceButtonDisabled}
                                startIcon={isInvoiceButtonDisabled ? <CircularProgress size={20} color="inherit" /> : undefined}
                            >
                                {isInvoiceButtonDisabled ? 'Resending Invoice...' : 'Resend Invoice'}
                            </StyledButton>
                            <StyledButton
                                data-testid="talent-invoice-edit-resend"
                                variant="outlined"
                                color="primary"
                                size="large"
                                onClick={() => setIsEdit(!isEdit)}
                                disabled={!paymentView.canResendInvoice}
                                sx={{ mt: 2 }}
                                className="no-fastclick"
                            >
                                {isEdit ? 'Cancel' : 'Edit and Resend Invoice'}
                            </StyledButton>
                            {jobData.invoice?.id && (
                                <StyledButton
                                    variant="outlined"
                                    color="primary"
                                    size="large"
                                    onClick={handleDownloadInvoicePdf}
                                    sx={{ mt: 2 }}
                                    className="no-fastclick"
                                    startIcon={isDownloadingInvoice ? <CircularProgress size={20} color="inherit" /> : undefined}
                                    disabled={isDownloadingInvoice}
                                >
                                    {isDownloadingInvoice ? 'Preparing PDF...' : 'Download Invoice'}
                                </StyledButton>
                            )}
                        </>
                    )}

                    <FixedLoader loading={createInvoiceLoading || resendInvoiceLoading || updateInvoiceLoading || getDistanceMatrixLoading || isDownloadingInvoice} />

                    <SimpleModal isOpen={showTimeWarningPopup}>
                        <TitleContainer>
                            <PopupTitle>Paid Time Starts at Posted Arrival Time</PopupTitle>
                            <CrossButtonBlack onClick={() => setShowTimeWarningPopup(false)} className="no-fastclick" />
                        </TitleContainer>
                        <ModalText>
                            Paid time for this shifts starts at the Arrival Time originally posted for this shift,
                            not before - regardless of when you arrive at the office.
                            <br /><br />
                            Have you verbally confirmed with office management to invoice for time starting before
                            their posted Arrival Time?
                        </ModalText>
                        <StyledButton
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={() => {
                                setHasConfirmedEarlyTime(true);
                                setShowTimeWarningPopup(false);
                                setValue('startTime', startTime);
                            }}
                            className="no-fastclick"
                        >
                            Yes
                        </StyledButton>
                        <StyledButton
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={() => {
                                setShowTimeWarningPopup(false);
                                setValue('startTime', jobData.shifts[0].postedStart);
                            }}
                            className="no-fastclick"
                            sx={{ mt: 2 }}
                        >
                            No
                        </StyledButton>
                    </SimpleModal>

                    <SimpleModal isOpen={showStripeModal}>
                        <TitleContainer>
                            <PopupTitle>Set up Express Payment</PopupTitle>
                            <CrossButtonBlack
                                onClick={handleStripeSetupClose}
                                className="no-fastclick"
                            />
                        </TitleContainer>
                        <ModalText>
                            Set up Express Payment for faster payouts?
                        </ModalText>
                        {isPostInvoiceStripeModal && isGHR && (
                            <ModalText style={{ color: '#d32f2f', fontWeight: 600, marginTop: '12px' }}>
                                Important! You must create a Stripe Express Account to get your additional bonus funds
                            </ModalText>
                        )}
                        <StripeExpressOpenError failure={stripeOpenFailure} testIdPrefix="talent-invoice" />
                        <StyledButton
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleStripeSetupOk}
                            disabled={stripeOpening}
                            data-testid="talent-invoice-stripe-setup-ok"
                            className="no-fastclick"
                        >
                            OK
                        </StyledButton>
                        <StyledButton
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={handleStripeSetupClose}
                            data-testid="talent-invoice-stripe-setup-no"
                            className="no-fastclick"
                            sx={{ mt: 2 }}
                        >
                            No
                        </StyledButton>

                    </SimpleModal>

                    <SimpleModal isOpen={showSuccessModal}>
                        <TitleContainer>
                            <PopupTitle>Invoice sent</PopupTitle>
                            <CrossButtonBlack
                                onClick={handleSuccessDismiss}
                                className="no-fastclick"
                            />
                        </TitleContainer>
                        <StyledButton
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleSuccessDismiss}
                            className="no-fastclick"
                            sx={{ mt: 2, alignSelf: 'flex-start' }}
                        >
                            OK
                        </StyledButton>
                    </SimpleModal>

                    <SimpleModal isOpen={showStripeUpdateModal}>
                        <TitleContainer>
                            <PopupTitle>Stripe Express Account Update Required</PopupTitle>
                            <CrossButtonBlack onClick={closeStripeUpdateModal} className="no-fastclick" />
                        </TitleContainer>
                        <ModalText>
                            We're taking you to your Stripe Express account to make the necessary updates. 
                            Once you're done, refresh this page to see the updated status of your Stripe Express account.
                        </ModalText>
                        <StripeExpressOpenError failure={stripeOpenFailure} testIdPrefix="talent-invoice" />
                        <StyledButton
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleStripeAccountUpdate}
                            disabled={stripeOpening}
                            data-testid="talent-invoice-stripe-update-ok"
                            className="no-fastclick"
                        >
                            OK
                        </StyledButton>
                    </SimpleModal>
                    <Snackbar
                        open={isDownloadErrorOpen}
                        autoHideDuration={4000}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        onClose={() => setIsDownloadErrorOpen(false)}
                    >
                        <Alert
                            severity="error"
                            onClose={() => setIsDownloadErrorOpen(false)}
                            sx={{ width: '100%' }}
                        >
                            {downloadError || 'Unable to download invoice. Please try again.'}
                        </Alert>
                    </Snackbar>
                </PageContainer>
            </>
        </LocalizationProvider>
    );
};

function invoiceRouteJobId(state: LocationState | undefined, search: string): number {
    const params = new URLSearchParams(search || window.location.search);
    const id = Number(state?.jobId ?? state?.job?.id ?? params.get('jobId') ?? params.get('id'));
    return Number.isSafeInteger(id) && id > 0 ? id : 0;
}

const HygienistInvoicePage: React.FC = () => {
    const history = useHistory();
    const location = useLocation<LocationState | undefined>();
    const { actor, currentActor } = useInvoiceDisputeActor('talent');
    const jobId = invoiceRouteJobId(location.state, location.search);
    const embeddedVisit = useRef({ key: location.key, actor });
    if (embeddedVisit.current.key !== location.key) embeddedVisit.current = { key: location.key, actor };
    if (!actor || !jobId) return null;

    // A notification can change only route state while this page stays mounted.
    // Remount the invoice form for each visit, including visits to the same job.
    const visit = `${actor}:${location.key || ''}:${location.pathname}:${location.search}:${jobId}`;
    const isVisitCurrent = () => currentActor() === actor
        && history.location.key === location.key
        && history.location.pathname === location.pathname
        && history.location.search === location.search
        && invoiceRouteJobId(history.location.state as LocationState | undefined, history.location.search) === jobId;
    const suppliedJob = embeddedVisit.current.actor === actor && location.state?.job?.id === jobId
        ? location.state.job : undefined;
    return <HygienistInvoiceVisit key={visit} jobId={jobId} suppliedJob={suppliedJob}
        preferredView={location.state?.isViewInvoice} isVisitCurrent={isVisitCurrent} />;
};

const HygienistInvoiceVisit = ({ jobId, suppliedJob, preferredView, isVisitCurrent }: {
    jobId: number; suppliedJob?: Job; preferredView?: boolean; isVisitCurrent: () => boolean;
}) => {
    const [jobData, setJobData] = useState<Job | null>(suppliedJob || null);
    const [isViewInvoice, setIsViewInvoice] = useState(!!preferredView);
    const [loading, setLoading] = useState(!suppliedJob);

    useEffect(() => {
        let cancelled = false;
        const isCurrent = () => !cancelled && isVisitCurrent();
        const fetchData = async () => {
            if (suppliedJob || !isCurrent()) return;
            try {
                const fetchedJob = await Api.getJobDetails(jobId);
                if (!isCurrent()) return;
                const invoice = await Api.getInvoiceForJob(jobId).catch(() => null);
                if (!isCurrent()) return;
                const currentHygienist = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist;
                const jobWithHygienist = {
                    ...fetchedJob,
                    hygienist: fetchedJob.hygienist || currentHygienist,
                } as Job;
                const invoiceObj = invoice && typeof invoice === 'object' ? (invoice as any) : null;
                const hasValidInvoice = !!(invoiceObj && (invoiceObj.id || invoiceObj?.invoice?.id));
                setJobData(hasValidInvoice ? { ...jobWithHygienist, invoice: invoiceObj } : jobWithHygienist);
                setIsViewInvoice(preferredView !== undefined ? !!preferredView : hasValidInvoice);
            } catch (error) {
                if (isCurrent()) console.error('Error fetching job or invoice data:', error);
            } finally {
                if (isCurrent()) setLoading(false);
            }
        };
        fetchData();
        return () => { cancelled = true; };
        // The keyed visit owns its initial route data and all pending reads.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading || !jobData) {
        return <FixedLoader loading={true} />;
    }

    return <HygienistInvoiceContent job={jobData} isViewInvoice={isViewInvoice} isVisitCurrent={isVisitCurrent} />;
};

export default HygienistInvoicePage;
